import React, { createContext, useContext, useState, useEffect } from 'react'
import axios from 'axios'
import { jwtDecode } from 'jwt-decode'
import { API_URL } from '../config/api'

interface User {
  id: string
  email: string
  name?: string
  image?: string
  role?: 'user' | 'super_admin' | 'site_content_manager'
  customer?: string
  allowedSites?: string[]
}

interface AuthContextType {
  user: User | null
  loading: boolean
  error: string | null
  token: string | null
  isAuthenticated: boolean
  login: (googleToken: string) => Promise<void>
  logout: () => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

const IS_DEV_MODE = import.meta.env.NODE_ENV === 'development' || import.meta.env.MODE === 'development'

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [token, setToken] = useState<string | null>(() => {
    // Initialize token from localStorage only if not force logged out
    const forceLogout = localStorage.getItem('force_logout') === 'true'
    return forceLogout ? null : localStorage.getItem('auth_token')
  })
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const initAuth = async () => {
      console.log('Initializing authentication state')
      
      // Check if user has explicitly logged out
      const forceLogout = localStorage.getItem('force_logout') === 'true'
      if (forceLogout) {
        console.log('User has explicitly logged out, preventing auto-login')
        // Clear any remaining auth data
        localStorage.removeItem('auth_token')
        sessionStorage.removeItem('auth_token')
        setToken(null)
        setUser(null)
        setLoading(false)
        return
      }
      
      // Check if we have a token in localStorage
      const storedToken = localStorage.getItem('auth_token')
      
      // Only use stored token if available
      if (storedToken && !token) {
        // If we have a token in localStorage but not in state, set it
        setToken(storedToken)
      }
      
      if (token) {
        try {
          // Validate token and get user info
          const decoded = jwtDecode<{ id?: string; sub?: string; email: string; name?: string; picture?: string; role?: 'user' | 'super_admin' | 'site_content_manager'; customer?: string; allowedSites?: string[]; exp?: number }>(token)
          const currentTime = Date.now() / 1000
          
          // Check if token is expired and we're not in dev mode
          if (decoded.exp && decoded.exp < currentTime && !IS_DEV_MODE) {
            console.log('Token expired, attempting to refresh')
            // Try to refresh the token by making an API call
            try {
              const response = await axios.get(`${API_URL}/auth/refresh`, {
                headers: { 'Authorization': `Bearer ${token}` }
              })
              
              if (response.data?.token) {
                // Store new token
                localStorage.setItem('auth_token', response.data.token)
                setToken(response.data.token)
                // Continue with the new token
                return
              }
            } catch (refreshErr) {
              console.error('Failed to refresh token:', refreshErr)
              // Continue with token cleanup
            }
            
            // Token expired and refresh failed
            localStorage.removeItem('auth_token')
            setToken(null)
            setUser(null)
          } else {
            // Set user from token
            setUser({
              id: decoded.id || decoded.sub || '',
              email: decoded.email,
              name: decoded.name,
              image: decoded.picture,
              role: decoded.role,
              customer: decoded.customer,
              allowedSites: decoded.allowedSites
            })
            
            // Setup axios auth header for all future requests
            axios.defaults.headers.common['Authorization'] = `Bearer ${token}`
            
            // Check for X-New-Token header in responses
            axios.interceptors.response.use(response => {
              const newToken = response.headers['x-new-token']
              if (newToken) {
                console.log('Received new token from server')
                localStorage.setItem('auth_token', newToken)
                setToken(newToken)
              }
              return response
            })
          }
        } catch (err) {
          console.error('Token validation error:', err)
          // Invalid token
          localStorage.removeItem('auth_token')
          setToken(null)
          setUser(null)
        }
      }
      setLoading(false)
    }

    initAuth()
  }, [token])

  const login = async (googleToken: string) => {
    setLoading(true)
    setError(null)
    
    try {
      console.log('Sending Google token to backend for authentication')
      console.log('API URL:', `${API_URL}/auth/google`)
      console.log('Token preview:', googleToken.substring(0, 50) + '...')
      
      // Exchange Google token for our JWT
      const response = await axios.post(`${API_URL}/auth/google`, { token: googleToken })
      
      console.log('Auth response:', response.data)
      console.log('Response status:', response.status)
      
      // Check for either token or jwt in the response (for compatibility)
      const authToken = response.data?.token || response.data?.jwt
      
      if (authToken) {
        console.log('JWT token received successfully')
        
        // Decode token first
        const decoded = jwtDecode<{ id?: string; sub?: string; email: string; name?: string; picture?: string; role?: 'user' | 'super_admin' | 'site_content_manager'; customer?: string; allowedSites?: string[] }>(authToken)
        console.log('Decoded token info:', { id: decoded.id || decoded.sub, email: decoded.email })
        
        // Store token
        localStorage.setItem('auth_token', authToken)
        
        // Clear any logout flags
        localStorage.removeItem('force_logout')
        
        // Setup axios auth header
        axios.defaults.headers.common['Authorization'] = `Bearer ${authToken}`
        
        // Create user object
        const newUser = {
          id: decoded.id || decoded.sub || '',
          email: decoded.email,
          name: decoded.name,
          image: decoded.picture,
          role: decoded.role,
          customer: decoded.customer,
          allowedSites: decoded.allowedSites
        }
        
        // Set both user and token immediately to ensure they're in sync
        setUser(newUser)
        setToken(authToken)
        
        // Force a small delay to ensure state is properly set before navigation
        await new Promise(resolve => setTimeout(resolve, 100))
        
        console.log('Login successful, user state set:', newUser)
      } else {
        console.error('No token received from server', response.data)
        throw new Error('Invalid response from server: No token received')
      }
    } catch (err: any) {
      console.error('Login error:', err)
      // Provide more detailed error message
      if (err.response) {
        console.error('Error response:', err.response.data)
        
        // Check if this is a waitlist response
        if (err.response.status === 403 && err.response.data?.waitlist) {
          setError(err.response.data.message || 'You have been added to the waitlist')
        } else {
          setError(`Authentication failed: ${err.response.data?.message || err.response.statusText || 'Server error'}`)
        }
      } else if (err.request) {
        console.error('No response received:', err.request)
        setError('Authentication failed: No response from server')
      } else {
        setError(`Authentication failed: ${err.message || 'Unknown error'}`)
      }
    } finally {
      setLoading(false)
    }
  }

  const logout = () => {
    console.log('Logging out user')
    
    // Clear all authentication data from storage
    localStorage.removeItem('auth_token')
    sessionStorage.removeItem('auth_token')
    localStorage.setItem('force_logout', 'true') // Add a flag to prevent auto-login in dev mode
    
    // Clear React state
    setToken(null)
    setUser(null)
    
    // Remove auth header from axios
    delete axios.defaults.headers.common['Authorization']
    
    // Reset axios interceptors to prevent token refreshing
    // Create a new instance of axios to clear all interceptors
    const axiosInstance = axios.create()
    axios.defaults = axiosInstance.defaults
    
    // Force a complete page reload to clear any in-memory state
    if (typeof window !== 'undefined') {
      console.log('Redirecting to login page and clearing state')
      // Use replace to navigate to login page
      window.location.replace('/login')
    }
  }

  const value = {
    user,
    loading,
    error,
    token,
    isAuthenticated: !!user,
    login,
    logout
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}